import { Pagination } from "antd";
import type { LucideIcon } from "lucide-react";
import { Coins, Gift, Ticket, TrendingUp, Wallet, X } from "lucide-react";
import { useMemo, useState } from "react";

import type {
  MemberAccountBalanceVO,
  MemberAccountLogVO,
  PageDataMemberAccountLogVO,
} from "@/api/member/type";
import { cn } from "@/lib/cn";
import { formatDate } from "@/utils";

import {
  ACCOUNT_CARD_LABELS,
  type AccountCardKey,
  formatAmount,
  formatDelta,
  getAccountLogTotalDelta,
  getCardDimensionLabel,
  isAccountLogMatchCard,
} from "./utils";

/** 账户 Tab props */
export interface AccountTabProps {
  /** 余额摘要 */
  balance?: MemberAccountBalanceVO;
  /** 升级所需成长值 */
  growthValueRequired?: number;
  /** 账户明细分页 */
  accountLogs?: PageDataMemberAccountLogVO;
  /** 明细翻页 */
  onLogPageChange: (page: number, pageSize: number) => void;
}

/** 摘要卡配置项 */
interface BalanceCardItem {
  key: AccountCardKey;
  label: string;
  value: string;
  icon: LucideIcon;
}

/**
 * 构建账户摘要卡片数据
 * @param balance 余额摘要
 * @param growthValueRequired 升级所需成长值
 */
const buildBalanceCards = (
  balance?: MemberAccountBalanceVO,
  growthValueRequired?: number,
): BalanceCardItem[] => [
  { key: "balance", label: "本金", value: formatAmount(balance?.principalBalance), icon: Wallet },
  { key: "gift", label: "赠送金", value: formatAmount(balance?.bonusBalance), icon: Gift },
  // { key: "credit", label: "授信", value: formatAmount(balance?.creditLimit), icon: CreditCard },
  { key: "points", label: "剩余积分", value: formatAmount(balance?.points), icon: Coins },
  {
    key: "growth",
    label: "成长值",
    value: balance?.growthValue
      ? `${formatAmount(balance?.growthValue)}/${formatAmount(growthValueRequired ?? 0)}`
      : "-",
    icon: TrendingUp,
  },
  {
    key: "coupon",
    label: "卡券",
    value: balance?.couponCount ? `${balance.couponCount}张` : "-",
    icon: Ticket,
  },
];

/**
 * 单条流水变动维度单元格
 * @param label 维度名
 * @param delta 变动值
 * @param after 变动后余额
 * @param highlight 是否为当前筛选维度
 * @param dimmed 非筛选维度时降透明度
 */
const DeltaCell = ({
  label,
  delta,
  after,
  highlight,
  dimmed,
}: {
  label: string;
  delta?: number;
  after?: number;
  highlight?: boolean;
  dimmed?: boolean;
}) => (
  <div
    className={cn(
      "text-center rounded-lg p-2 transition-all min-w-0",
      highlight && "bg-orange-500/15 ring-1 ring-orange-500/30",
      dimmed && "opacity-30",
    )}
  >
    <p className="text-slate-400 mb-1 text-[11px]">{label}</p>
    <p className="font-semibold text-sm text-slate-800 tabular-nums">{formatDelta(delta)}</p>
    <p className="text-slate-400/80 text-[10px] mt-0.5 tabular-nums">
      余{after === undefined ? "-" : formatAmount(after)}
    </p>
  </div>
);

/**
 * 按合计变动取左侧色条样式
 * @param total 合计变动
 */
const getLogAccentClass = (total: number) => {
  if (total > 0) return "bg-emerald-500";
  if (total < 0) return "bg-red-500";
  return "bg-slate-300";
};

/**
 * 账户变动明细卡片
 * @param log 流水
 * @param selectedCard 当前筛选的摘要卡
 */
const AccountLogCard = ({
  log,
  selectedCard,
}: {
  log: MemberAccountLogVO;
  selectedCard: AccountCardKey | null;
}) => {
  // 右上角展示用的合计变动
  const total = getAccountLogTotalDelta(log.principalDelta, log.bonusDelta, log.pointsDelta);
  // 筛选维度中文名，用于列高亮
  const activeLabel = selectedCard ? getCardDimensionLabel(selectedCard) : null;
  // 五维变动单元格配置
  const cells = [
    { label: "本金", delta: log.principalDelta, after: log.principalBalanceAfter },
    { label: "赠送金", delta: log.bonusDelta, after: log.bonusBalanceAfter },
    { label: "授信", delta: log.creditLimitDelta, after: log.creditAvailableAfter },
    { label: "积分", delta: log.pointsDelta, after: log.pointsAfter },
    { label: "成长", delta: log.growthValueDelta, after: log.growthValueAfter },
  ];

  return (
    <div className="rounded-lg bg-white p-3 shadow-[0_4px_16px_rgba(0,0,0,0.08),0_1px_4px_rgba(0,0,0,0.04)]">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className={cn("w-1 h-4 rounded-full shrink-0", getLogAccentClass(total))} />
          <span className="text-sm font-medium text-slate-800 truncate">
            {log.bizTypeLabel || log.bizType || "变动"}
          </span>
        </div>
        <span
          className={cn(
            "text-sm font-medium tabular-nums shrink-0",
            total > 0 && "text-emerald-600",
            total < 0 && "text-red-600",
            total === 0 && "text-slate-400",
          )}
        >
          {total === 0 ? "-" : formatDelta(total)}
        </span>
      </div>
      <div className="text-xs text-slate-400 mb-2 truncate">
        {formatDate(log.occurredAt, "YYYY-MM-DD HH:mm")}
        {log.orderNo ? ` ${log.orderNo}` : ""}
      </div>
      <div className="bg-slate-50 rounded-md p-2 grid grid-cols-5 gap-1.5">
        {cells.map((cell) => (
          <DeltaCell
            key={cell.label}
            label={cell.label}
            delta={cell.delta}
            after={cell.after}
            highlight={Boolean(activeLabel && cell.label === activeLabel)}
            dimmed={Boolean(activeLabel && cell.label !== activeLabel)}
          />
        ))}
      </div>
    </div>
  );
};

/**
 * 会员详情 - 账户 Tab：余额摘要（可筛选）+ 变动明细
 */
export const AccountTab = ({
  balance,
  growthValueRequired,
  accountLogs,
  onLogPageChange,
}: AccountTabProps) => {
  // 当前选中的摘要卡；null 表示查看全部明细
  const [selectedCard, setSelectedCard] = useState<AccountCardKey | null>(null);
  const cards = buildBalanceCards(balance, growthValueRequired);
  // 接口返回的明细总条数（未按卡筛选）
  const total = accountLogs?.total ?? 0;

  // 按选中摘要卡在当前页内筛选流水（records 默认值放进 callback，避免 ?? [] 每渲染新建引用）
  const filteredRecords = useMemo(() => {
    const records = accountLogs?.records ?? [];
    if (!selectedCard || selectedCard === "coupon") return records;
    return records.filter((log) => isAccountLogMatchCard(log, selectedCard));
  }, [accountLogs?.records, selectedCard]);

  /** 切换摘要卡选中；再次点击同一卡则清除 */
  const handleCardClick = (key: AccountCardKey) => {
    setSelectedCard((prev) => (prev === key ? null : key));
  };

  return (
    <div className="flex flex-col h-full min-h-0 gap-4">
      {/* 弹性盒横向平分，卡片数量变化时仍均分宽度 */}
      <div className="flex gap-2 shrink-0">
        {cards.map((card) => {
          const Icon = card.icon;
          // 是否为当前筛选卡
          const active = selectedCard === card.key;
          return (
            <button
              key={card.key}
              type="button"
              onClick={() => handleCardClick(card.key)}
              className={cn(
                "flex-1 min-w-0 rounded-lg bg-white p-2 text-left shadow-[0_4px_16px_rgba(0,0,0,0.08),0_1px_4px_rgba(0,0,0,0.04)] cursor-pointer transition-all",
                "hover:shadow-[0_8px_24px_rgba(0,0,0,0.12),0_2px_8px_rgba(0,0,0,0.06)]",
                active ? "ring-2 ring-orange-500/40 bg-orange-50/30" : "hover:bg-slate-50/80",
              )}
            >
              <div className="flex items-center gap-1 mb-1">
                <Icon className="w-3 h-3 text-slate-400 shrink-0" />
                <span className="text-xs text-slate-400 truncate">{card.label}</span>
              </div>
              <p className="text-base font-semibold text-slate-900 tabular-nums m-0 leading-tight">
                {card.value}
              </p>
            </button>
          );
        })}
      </div>

      <div className="flex flex-col flex-1 min-h-0">
        <div className="flex items-center justify-between mb-2 shrink-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-slate-500">
              {selectedCard ? `${ACCOUNT_CARD_LABELS[selectedCard]}变动明细` : "全部变动明细"}
            </span>
            {selectedCard && (
              <button
                type="button"
                onClick={() => setSelectedCard(null)}
                className="text-xs text-slate-400 hover:text-slate-700 inline-flex items-center gap-0.5"
              >
                <X className="w-3 h-3" />
                清除筛选
              </button>
            )}
          </div>
          <span className="text-xs text-slate-400">
            共{selectedCard && selectedCard !== "coupon" ? filteredRecords.length : total}条
          </span>
        </div>

        {/*
          明细列表滚动区：flex 吃满剩余高度；
          max-height 兜底（扣掉头图/摘要卡/分页等），避免 Tabs/Spin 打断高度链导致无法滚动
        */}
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain max-h-[calc(80vh-380px)]">
          {selectedCard === "coupon" ? (
            <div className="h-full min-h-40 bg-slate-50 rounded-lg flex items-center justify-center text-sm text-slate-400">
              暂无卡券明细
            </div>
          ) : filteredRecords.length === 0 ? (
            <div className="h-full min-h-40 bg-slate-50 rounded-lg flex items-center justify-center text-sm text-slate-400">
              暂无{selectedCard ? ACCOUNT_CARD_LABELS[selectedCard] : ""}相关明细
            </div>
          ) : (
            <div className="space-y-2 pr-1">
              {filteredRecords.map((log) => (
                <AccountLogCard key={log.logId} log={log} selectedCard={selectedCard} />
              ))}
            </div>
          )}
        </div>

        {total > 0 && selectedCard !== "coupon" && (
          <div className="flex justify-end mt-3 shrink-0">
            <Pagination
              size="small"
              current={accountLogs?.current ?? 1}
              pageSize={accountLogs?.size ?? 10}
              total={total}
              onChange={onLogPageChange}
              showSizeChanger={false}
            />
          </div>
        )}
      </div>
    </div>
  );
};
